import retry from 'async-retry';
import _ from 'lodash';
import type {
  AnalyticsTrackType,
  Product,
  UserInfo
} from '../model/wac-signup-form';
import { ProductKeys } from '../model/wac-signup-form';
import { SignUpFormAPI } from '../layouts/legacy/view/sign-up-form';
import { sanitizeLastName } from '../layouts/legacy/view/utility';

const BXP_CLOUD_PERMS_CHECK = 'bxp.cloud-signup.rename.site.permissions.check';
const BXP_CLOUD_PERMS_CHECK_FALSE =
  'bxp.cloud-signup.rename.site.permissions.check.failed';

const getDecodedSocialSignupInfo = (
  encodedSocialSignupInfo: any | null | undefined
) => {
  if (encodedSocialSignupInfo) {
    return {
      ...encodedSocialSignupInfo,
      email: decodeURIComponent(encodedSocialSignupInfo.email),
      firstName: decodeURIComponent(encodedSocialSignupInfo.firstName),
      fullName: decodeURIComponent(encodedSocialSignupInfo.fullName),
      lastName: decodeURIComponent(encodedSocialSignupInfo.lastName)
    };
  } else {
    return null;
  }
};

type GetRenameSiteUrlType = {
  wacEnvUrl: string;
  confirmationPageUrl: string;
  cloudId: string;
  cloudName: string;
  productKeys: string[];
  progressUri: string;
  isPermitted: boolean;
  siteRenamePageUrl: string;
  hideUserSegmentation?: boolean;
};

export function getRenameSiteUrl({
  wacEnvUrl,
  confirmationPageUrl,
  cloudId,
  cloudName,
  productKeys,
  progressUri,
  isPermitted,
  siteRenamePageUrl,
  hideUserSegmentation
}: GetRenameSiteUrlType) {
  const confirmationUrl = SignUpFormAPI.generateConfirmationUrl({
    wacEnvUrl,
    confirmationPageUrl,
    cloudId,
    progressUri,
    cloudName,
    products: productKeys.join(','),
    hideUserSegmentation
  });

  // Build rename site url
  return SignUpFormAPI.generateRenameUrl(
    wacEnvUrl,
    cloudId,
    progressUri,
    confirmationUrl,
    siteRenamePageUrl,
    isPermitted
  );
}

export const localStorageGet = (key: string): string => {
  try {
    return localStorage.getItem(key) || '';
  } catch (e) {
    return '';
  }
};

export function checkIfVisited(
  userInfo: UserInfo,
  setShowSignupForm: (val: boolean) => void
) {
  if (sessionStorage) {
    const storage =
      window.atob(sessionStorage.getItem('social-signup') || '') || null;
    let socialSignupInfo = null;
    if (storage !== null) {
      socialSignupInfo = JSON.parse(storage);

      const decodedSocialSignupInfo =
        getDecodedSocialSignupInfo(socialSignupInfo);
      if (decodedSocialSignupInfo !== null) {
        if (_.isEqual(userInfo, decodedSocialSignupInfo.userInfo)) {
          // user is the same, redirect to stored rename url
          window.location.replace(decodedSocialSignupInfo.redirectUrl);
        } else {
          // user is not the same, remove session storage and show signup form
          sessionStorage.removeItem('social-signup');
          setShowSignupForm(true);
        }
        return true;
      }
    }
  }
  return false;
}

export function getStatuspageAdvancedSettings(isStaging: boolean) {
  return {
    advancedSettings: {
      initialProductURL: isStaging
        ? 'manage.statuspagestaging.com'
        : 'manage.statuspage.io',
      additionalProvisioningData: {
        signupLocation: 'WAC',
        spProduct: 'public'
      }
    }
  };
}

export const getEncodedUserInfo = (unencodedUserInfo: UserInfo) => ({
  ...unencodedUserInfo,
  email: encodeURIComponent(unencodedUserInfo.email || ''),
  firstName: encodeURIComponent(unencodedUserInfo.firstName || ''),
  fullName: encodeURIComponent(unencodedUserInfo.fullName || ''),
  lastName: encodeURIComponent(unencodedUserInfo.lastName || '')
});

const trackPermsNR = (
  analyticsTrack: AnalyticsTrackType,
  cloudId: string,
  status: any,
  retryCount: number,
  permitted?: any
) => {
  analyticsTrack.newRelic.action(
    BXP_CLOUD_PERMS_CHECK,
    JSON.stringify({
      cloudId,
      status,
      retryCount,
      permitted
    })
  );
};

export function checkPerms(
  cloudId: string,
  wacEnvUrl: string,
  analyticsTrack: AnalyticsTrackType
) {
  let retryCount = 1;
  return retry(
    async (bail) => {
      // if anything throws, we retry
      const postBody = {
        dontRequirePrincipalInSite: 'true',
        permissionId: 'manage',
        resourceId: `ari:cloud:platform::site/${cloudId}`
      };

      const options = {
        headers: {
          'content-type': 'application/json'
        },
        method: 'POST',
        body: JSON.stringify(postBody)
      };
      const perms = await fetch(
        `${wacEnvUrl}/gateway/api/permissions/permitted`,
        options
      );

      if (
        403 === perms.status ||
        401 === perms.status ||
        500 === perms.status
      ) {
        trackPermsNR(analyticsTrack, cloudId, perms.status, retryCount);
        bail(new Error('Unauthorized or Server error'));
        return;
      }
      // retry if Perms returned http != 200
      if (200 !== perms.status) {
        trackPermsNR(analyticsTrack, cloudId, perms.status, retryCount);

        retryCount++;
        throw new Error(BXP_CLOUD_PERMS_CHECK_FALSE);
      }

      const data = await perms.json();
      trackPermsNR(
        analyticsTrack,
        cloudId,
        perms.status,
        retryCount,
        data.permitted
      );

      // retry if Perms returned http 200 with permitted=false
      if (!(data.permitted === true)) {
        retryCount++;
        throw new Error(BXP_CLOUD_PERMS_CHECK_FALSE);
      }
      return data;
    },
    {
      retries: 4,
      minTimeout: 1000,
      maxTimeout: 1000
    }
  );
}

export function getProductKeys(products: Product[]) {
  return products.map(({ product }) => product as string);
}

export function filterEligibleSites(
  userInfo: UserInfo,
  productKeysSelectedForProvisioning: string[]
) {
  const { sites: userSites, extendedTrialOverridableSites } = userInfo;
  return !!userSites
    ? userSites?.filter((existingUserSite: { products: any; cloudId: any }) => {
        const { products: existingProductKeysOnSite, cloudId } =
          existingUserSite;

        return (
          !productKeysSelectedForProvisioning.some((productKey) =>
            existingProductKeysOnSite.includes(productKey)
          ) || extendedTrialOverridableSites?.includes(cloudId)
        );
      })
    : [];
}

export function buildSocialSignUpPayload(
  userInfo: UserInfo,
  productsSelectedForProvisioning: Product[],
  templateId: string | undefined,
  devMode: boolean,
  isStaging: boolean,
  hideUserSegmentation: boolean
) {
  const { email, firstName, lastName } = userInfo;

  // Some users may not have a last name due to registration by Full Name in some flows
  // COFS requires a last name currently, so we set lastname  '.' in this case
  const sanitizedLastName = sanitizeLastName(lastName);

  const signupContext = {};
  Object.assign(signupContext, {
    pagePath: 'rename/site',
    autogenSiteName: 1
  });

  // Pass in template id for on-boarding in app (Confluence only currently)
  if (templateId) {
    Object.assign(signupContext, { templateId });
  }

  const payload = {
    products: productsSelectedForProvisioning.map(
      ({ product: productKey, edition }) => ({
        product: productKey,
        edition
      })
    ),
    adminUsername: 'admin',
    cloudName: null,
    email,
    firstName,
    lastName: sanitizedLastName
  };
  Object.assign(payload, {
    signupContext: JSON.stringify(signupContext)
  });

  const isStatuspage = productsSelectedForProvisioning.some(
    ({ product }) => product === ProductKeys.STATUSPAGE
  );
  if (isStatuspage) {
    _.merge(payload, getStatuspageAdvancedSettings(isStaging));
  }
  if (hideUserSegmentation) {
    _.merge(payload, {
      advancedSettings: {
        additionalProvisioningData: {
          hideUserSegmentation: true
        }
      }
    });
  }

  // Pass in known Experiment Key to COFS to activate Standard Developer license
  if (devMode) {
    const DEVELOPER_EXPERIMENT_KEY = 'PUR-7747';
    Object.assign(payload, {
      experiment: {
        experimentKey: DEVELOPER_EXPERIMENT_KEY
      }
    });
  }

  return payload;
}
